
export default function WhySmallBusinessesNeedWebsitesSection() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Why Every Small Business in El Paso <span className="text-teal-600">Needs a Website</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            In today's digital-first world, your website is often the first impression potential customers have of your business. 
            Here's why having a professional website is crucial for your El Paso small business success.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/ec53a6afd51475ea18173db0d303c376.jpg"
              alt="Mobile search results showing local El Paso businesses"
              className="w-full h-80 object-cover rounded-2xl shadow-xl"
            />
          </div>
          
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">97% of Customers Search Online First</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Before visiting a local business in El Paso, 97% of customers search online to find information, 
              read reviews, and compare options. Without a website, you're invisible to these potential customers 
              who are actively looking for your services.
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Your website acts as your digital storefront, providing essential information like your location, 
              hours, services, and contact details. It's your opportunity to make a great first impression and 
              convince customers to choose your business over competitors.
            </p>
            <div className="bg-teal-50 p-6 rounded-xl">
              <div className="text-2xl font-bold text-teal-600 mb-2">Local Search Opportunity</div>
              <p className="text-gray-700">
                "Near me" searches have increased by over 900% in the past two years. Make sure your El Paso 
                business appears when locals search for your services.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shield-check-line text-2xl text-teal-600"></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-900 mb-4">Build Trust & Credibility</h4>
            <p className="text-gray-600 leading-relaxed">
              A professional website instantly establishes credibility with potential customers. Studies show that 
              75% of consumers judge a business's credibility based on their website design. Without a website, 
              customers may question your legitimacy and choose competitors instead.
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-time-line text-2xl text-teal-600"></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-900 mb-4">24/7 Customer Service</h4>
            <p className="text-gray-600 leading-relaxed">
              Your website works around the clock, providing information, answering common questions, and 
              generating leads even when your business is closed. This means potential customers can learn 
              about your services and contact you at any time, increasing your opportunities for new business.
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-line-chart-line text-2xl text-teal-600"></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-900 mb-4">Cost-Effective Marketing</h4>
            <p className="text-gray-600 leading-relaxed">
              A website is one of the most cost-effective marketing tools available to small businesses. 
              Unlike traditional advertising that requires ongoing payments, your website provides continuous 
              marketing value, helping you attract customers and grow your business for years to come.
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Compete with Larger Businesses
              </h3>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                A well-designed website levels the playing field between small businesses and larger competitors. 
                With professional design, compelling content, and strategic SEO optimization, your small business 
                can compete effectively in the El Paso market and attract customers who might otherwise choose 
                bigger companies.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Your website allows you to showcase your unique value proposition, highlight your personal service, 
                and demonstrate the advantages of working with a local El Paso business. This personal touch often 
                resonates more with customers than generic corporate websites.
              </p>
              <div className="flex space-x-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600 mb-2">68%</div>
                  <div className="text-sm text-gray-600">Prefer Local Businesses</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600 mb-2">84%</div>
                  <div className="text-sm text-gray-600">Trust Online Reviews</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600 mb-2">92%</div>
                  <div className="text-sm text-gray-600">Visit After Online Search</div>
                </div>
              </div>
            </div>
            
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=small%20local%20business%20competing%20with%20large%20corporations%2C%20David%20vs%20Goliath%20concept%2C%20professional%20business%20growth%20visualization%2C%20modern%20clean%20design%2C%20competitive%20advantage%20illustration%2C%20success%20metaphor&width=600&height=500&seq=competition-4&orientation=landscape"
                alt="Small businesses competing effectively with professional websites"
                className="w-full h-80 object-cover rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
